/**
 * Maps Bedrock's curated `icon_name` values to Heroicon outline components.
 *
 * The authoritative list lives on Bedrock (`GET /api/v1/apps/icon-names`, 43 entries today) and
 * may grow — this map is a *rendering* concern only. Anything unmapped falls back to the generic
 * squares glyph rather than breaking a tile, so a newly-added Bedrock icon degrades gracefully
 * instead of crashing the switcher.
 */

import {
  BellAlertIcon,
  BellIcon,
  BookOpenIcon,
  BriefcaseIcon,
  BuildingOffice2Icon,
  ChartBarIcon,
  ChartPieIcon,
  ChatBubbleLeftRightIcon,
  CheckCircleIcon,
  ClipboardDocumentListIcon,
  ClockIcon,
  CloudIcon,
  CodeBracketIcon,
  Cog6ToothIcon,
  CommandLineIcon,
  ComputerDesktopIcon,
  CubeIcon,
  DocumentTextIcon,
  EnvelopeIcon,
  EyeIcon,
  FingerPrintIcon,
  FolderIcon,
  GlobeAltIcon,
  InboxStackIcon,
  KeyIcon,
  LifebuoyIcon,
  LightBulbIcon,
  LockClosedIcon,
  MapIcon,
  NewspaperIcon,
  PresentationChartLineIcon,
  PuzzlePieceIcon,
  QueueListIcon,
  RectangleGroupIcon,
  RocketLaunchIcon,
  ServerStackIcon,
  ShieldCheckIcon,
  SparklesIcon,
  Squares2X2Icon,
  TicketIcon,
  UserGroupIcon,
  UsersIcon,
  WrenchScrewdriverIcon,
} from '@heroicons/react/24/outline';
import type { ComponentType, SVGProps } from 'react';

type IconComponent = ComponentType<SVGProps<SVGSVGElement>>;

const ICON_MAP: Record<string, IconComponent> = {
  bell: BellIcon,
  'bell-alert': BellAlertIcon,
  'book-open': BookOpenIcon,
  briefcase: BriefcaseIcon,
  'building-office-2': BuildingOffice2Icon,
  'chart-bar': ChartBarIcon,
  'chart-pie': ChartPieIcon,
  'chat-bubble-left-right': ChatBubbleLeftRightIcon,
  'check-circle': CheckCircleIcon,
  'clipboard-document-list': ClipboardDocumentListIcon,
  clock: ClockIcon,
  cloud: CloudIcon,
  'code-bracket': CodeBracketIcon,
  'cog-6-tooth': Cog6ToothIcon,
  'command-line': CommandLineIcon,
  'computer-desktop': ComputerDesktopIcon,
  cube: CubeIcon,
  'document-text': DocumentTextIcon,
  envelope: EnvelopeIcon,
  eye: EyeIcon,
  'finger-print': FingerPrintIcon,
  folder: FolderIcon,
  'globe-alt': GlobeAltIcon,
  'inbox-stack': InboxStackIcon,
  key: KeyIcon,
  lifebuoy: LifebuoyIcon,
  'light-bulb': LightBulbIcon,
  'lock-closed': LockClosedIcon,
  map: MapIcon,
  newspaper: NewspaperIcon,
  'presentation-chart-line': PresentationChartLineIcon,
  'puzzle-piece': PuzzlePieceIcon,
  'queue-list': QueueListIcon,
  'rectangle-group': RectangleGroupIcon,
  'rocket-launch': RocketLaunchIcon,
  'server-stack': ServerStackIcon,
  'shield-check': ShieldCheckIcon,
  sparkles: SparklesIcon,
  'squares-2x2': Squares2X2Icon,
  ticket: TicketIcon,
  'user-group': UserGroupIcon,
  users: UsersIcon,
  'wrench-screwdriver': WrenchScrewdriverIcon,
};

export function resolveAppIcon(iconName: string | null | undefined): IconComponent {
  if (iconName && ICON_MAP[iconName]) {
    return ICON_MAP[iconName];
  }
  return Squares2X2Icon;
}

export const KNOWN_ICON_NAMES = Object.keys(ICON_MAP);
